import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import FnrButton, { ButtonIcon, ButtonText } from "UI/FnrButton3";
import FnrConfirmationPopover from "UI/FnrConfirmationPopover";

import API from "../../API";
import { useRecurringGifts } from "../../RecurringGiftsContext";
import { formatDateString } from "../../Shared/formatDateString";

export default function SkipButton({ triggerAnimation, setNoticeMessage }) {
  const { state, dispatch } = useRecurringGifts();
  const {
    ID,
    paymentDates,
    paymentType,
    status: giftStatus,
  } = state.editModal.currentGift;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [status, setStatus] = useState(null);
  const triggerRef = useRef();

  const disableButton = giftStatus !== "active" || paymentDates?.length === 0;

  const paymentNoun = paymentType || "Payment";
  const firstUpcomingPaymentDate = paymentDates ? paymentDates[0] : "N/A";
  const secondUpcomingPaymentDate = paymentDates ? paymentDates[1] : null;

  const handleInitialClick = () => {
    setShowConfirmation(!showConfirmation);
  };

  const handleCancelClick = () => {
    setShowConfirmation(false);
    setStatus(null);
  };

  const handleConfirmClick = () => {
    setStatus("loading");

    API.skip({ ID })
      .post()
      .then((res) => {
        dispatch({
          type: "handleGetRecurringGiftsResponse",
          payload: res,
        });
        setShowConfirmation(false);
        setStatus(null);
        triggerAnimation();
        setNoticeMessage(null);
      })
      .catch((err) => {
        setStatus("error");
      });
  };

  const confirmationContent = () => {
    return (
      <ConfirmContent>
        {status === "error" ? (
          <Error>
            Oh no! A problem occurred. Please refresh and try again, or contact
            help@flocknote.com for assistance.
          </Error>
        ) : null}
        <ConfirmLine1>
          Are you sure you want to skip the {paymentNoun} on{" "}
          <NoWrap>
            {formatDateString(firstUpcomingPaymentDate, {
              includeYear: true,
            })}
          </NoWrap>
          ?
        </ConfirmLine1>
        {secondUpcomingPaymentDate ? (
          <ConfirmLine2>
            The next {paymentNoun} will be on{" "}
            <NoWrap>
              {formatDateString(secondUpcomingPaymentDate, {
                includeYear: true,
              })}
            </NoWrap>
            .
          </ConfirmLine2>
        ) : null}
      </ConfirmContent>
    );
  };

  return (
    <Wrapper>
      <FnrButton
        data-cy="skipRecurringBtn"
        ref={triggerRef}
        onClick={handleInitialClick}
        size="small"
        fullWidth
        disabled={disableButton}
      >
        <ButtonIcon fas fa-share-all />
        <ButtonText>Skip Next</ButtonText>
      </FnrButton>

      <FnrConfirmationPopover
        show={showConfirmation}
        ref={triggerRef}
        onClose={() => setShowConfirmation(false)}
        message={confirmationContent()}
        button1={
          <FnrButton
            text="Cancel"
            onClick={handleCancelClick}
            disabled={status === "loading"}
          />
        }
        button2={
          <FnrButton
            text={`Yes, skip ${formatDateString(firstUpcomingPaymentDate)}`}
            onClick={handleConfirmClick}
            color="blue"
            loading={status === "loading"}
          />
        }
        style={{ maxWidth: 330, padding: 20 }}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  flex: 1;
`;

const ConfirmContent = styled.div`
  line-height: 1.4;
  margin-bottom: 20px;
`;

const ConfirmLine1 = styled.div`
  font-weight: 600;
  font-size: 18px;
`;

const ConfirmLine2 = styled.div`
  margin-top: 10px;
  font-size: 15px;
  opacity: 0.8;
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;

const Error = styled.div`
  background-color: #f7f0b6;
  line-height: 1.2;
  font-size: 14px;
  color: #ad8114;
  font-weight: 400;
  padding: 10px 14px;
  border-radius: 7px;
  margin-bottom: 10px;
`;
